import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from 'src/app/services/token/token.service'; // Adjust the path as needed
import { Router } from '@angular/router';
import { SessionInstanceService } from 'src/app/services/session/session-instance.service';
import { ValidateActiveSession } from 'src/app/_shared/consts/app-constants';

@Injectable()
export class CommonInterceptor implements HttpInterceptor {
  constructor(
    private tokenService: TokenService,
    private router: Router,
    private sessionInstanceService: SessionInstanceService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }
    if (this.tokenService.isTokenExpired()) {
      return this.handle401Error(req, next);
    }
    const authReq = this.addTokenToRequest(req);
    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // A newer CS tab (different profile) has taken over the shared session. Prompt the user
        // instead of surfacing a raw profile-mismatch error, then redirect to MPC.
        if (error.status === 409 && error.error?.code === ValidateActiveSession.NewerSessionDetectedCode) {
          this.sessionInstanceService.markStaleAndPrompt();
          return throwError(error);
        }
        if (error.status === 401 && !this.isAuthEndpoint(req.url)) {
          return this.handle401Error(req, next);
        }
        return throwError(error);
      })
    );
  }

  private addTokenToRequest(req: HttpRequest<any>): HttpRequest<any> {
    const setHeaders: { [name: string]: string } = {};
    const token = this.tokenService.getAccessToken();
    if (token && !this.tokenService.isTokenExpired()) {
      setHeaders['Authorization'] = `Bearer ${token}`;
    }
    // Identify which tab/session this request belongs to so the backend can reject a stale tab.
    const tabSessionId = this.sessionInstanceService.getTabSessionId();
    if (tabSessionId) {
      setHeaders[ValidateActiveSession.SessionIdHeader] = tabSessionId;
    }
    return Object.keys(setHeaders).length ? req.clone({ setHeaders }) : req;
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Delegate to TokenService's cross-tab single-flight refresh. Concurrent requests in
    // this tab, and refreshes in other tabs, all share / serialise through one refresh, so
    // we never trigger competing refreshes that would hit Midgard's one-time-use rotation.
    return this.tokenService.refreshToken().pipe(
      switchMap((result) => {
        if (result && result.access_token) {
          return next.handle(this.addTokenToRequest(req));
        }
        // Refresh genuinely failed (after retry + cross-tab coordination): session is gone.
        this.tokenService.routeToSessionExpired();
        return throwError(() => new Error('Session expired'));
      }),
      catchError((error) => {
        this.tokenService.routeToSessionExpired();
        return throwError(() => error);
      })
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('/auth/InitializeUserSession') ||
      url.includes('/auth/getnewtoken') ||
      url.includes('/auth/getReturnUrl');
  }
}
